import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaTimes, FaMoon, FaSun, FaUserShield, FaLanguage, FaDownload } from 'react-icons/fa';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from 'react-i18next';
import './SideMenu.css';

const SideMenu = ({ isOpen, onClose }) => {
  const { theme, toggleTheme } = useTheme();
  const { isAuthenticated, logout, user } = useAuth();
  const { selectedLanguage, setShowLanguageModal } = useLanguage();
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    // Check if it's iPhone specifically (not iPad or iPod)
    const isIPhone = /iPhone/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIPhone);

    // Check if app is already installed (standalone mode)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                         window.navigator.standalone === true;

    // For iPhone only, show install option if not already installed
    if (isIPhone && !isStandalone) {
      setIsInstallable(true);
    }

    // Listen for the beforeinstallprompt event (Android, Desktop Chrome, Edge)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for successful installation
    window.addEventListener('appinstalled', () => {
      setIsInstallable(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleLanguageClick = () => {
    setShowLanguageModal(true);
  };

  const handleInstallClick = async () => {
    if (isIOS) {
      // Show iOS instructions
      setShowIOSInstructions(true);
    } else if (deferredPrompt) {
      // Show install prompt for Android/Desktop
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        setIsInstallable(false);
      }
    }
  };

  const closeIOSInstructions = () => {
    setShowIOSInstructions(false);
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && <div className="side-menu-overlay" onClick={onClose}></div>}

      {/* Side Menu */}
      <div className={`side-menu ${isOpen ? 'open' : ''}`}>
        {/* Header */}
        <div className="side-menu-header">
          <h2>{t('menu')}</h2>
          <button className="close-btn" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* Menu Items */}
        <div className="side-menu-content">
          {/* User Info */}
          {isAuthenticated && user && (
            <div className="user-info">
              <div className="user-avatar">
                {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
              </div>
              <div className="user-details">
                <div className="user-name">{user.name || 'User'}</div>
                <div className="user-email">{user.email}</div>
              </div>
            </div>
          )}

          {/* Menu List */}
          <div className="menu-list">
            {/* Install App */}
            {isInstallable && (
              <button className="menu-item" onClick={handleInstallClick}>
                <span className="menu-icon">
                  <FaDownload />
                </span>
                <span className="menu-label">{t('install_app') || 'Install App'}</span>
              </button>
            )}

            {/* Language Selection */}
            <button className="menu-item" onClick={handleLanguageClick}>
              <span className="menu-icon">
                <FaLanguage />
              </span>
              <span className="menu-label">{t('select_language')}</span>
            </button>

            {/* Theme Toggle */}
            <button className="menu-item" onClick={toggleTheme}>
              <span className="menu-icon">
                {theme === 'light' ? <FaMoon /> : <FaSun />}
              </span>
              <span className="menu-label">
                {theme === 'light' ? t('dark_mode') : t('light_mode')}
              </span>
            </button>

            {/* Admin Link (if authenticated) */}
            {isAuthenticated && (
              <Link to="/admin" className="menu-item" onClick={onClose}>
                <span className="menu-icon">
                  <FaUserShield />
                </span>
                <span className="menu-label">{t('admin_panel')}</span>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* iOS Install Instructions Modal */}
      {showIOSInstructions && (
        <div className="ios-install-modal-overlay" onClick={closeIOSInstructions}>
          <div className="ios-install-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ios-install-header">
              <h3>{t('install_app') || 'Install App on iPhone'}</h3>
              <button className="ios-close-btn" onClick={closeIOSInstructions}>
                <FaTimes />
              </button>
            </div>
            <div className="ios-install-content">
              <p>{t('ios_install_instruction_1') || 'Follow these steps to add this app to your iPhone Home Screen:'}</p>
              <ol>
                <li>
                  <span className="ios-step-number">1</span>
                  <span className="ios-step-text">
                    {t('ios_install_step_1') || 'Tap the Share button '}
                    <span className="ios-share-icon">
                      <svg width="18" height="22" viewBox="0 0 18 22" fill="none">
                        <path d="M9 0L9 14M9 0L5 4M9 0L13 4" stroke="currentColor" strokeWidth="2"/>
                        <rect x="0" y="16" width="18" height="6" rx="1" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </span>
                    {t('ios_install_step_1_suffix') || ' at the bottom of Safari browser'}
                  </span>
                </li>
                <li>
                  <span className="ios-step-number">2</span>
                  <span className="ios-step-text">
                    {t('ios_install_step_2') || 'Scroll down and tap on "Add to Home Screen"'}
                  </span>
                </li>
                <li>
                  <span className="ios-step-number">3</span>
                  <span className="ios-step-text">
                    {t('ios_install_step_3') || 'Tap "Add" button in the top right corner to confirm'}
                  </span>
                </li>
                <li>
                  <span className="ios-step-number">4</span>
                  <span className="ios-step-text">
                    {t('ios_install_step_4') || 'The app icon will appear on your Home Screen and you can open it like any other app!'}
                  </span>
                </li>
              </ol>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SideMenu;
